import type { LoadProgress } from "@/lib/libreoffice-loader";

// The one-time WASM download is covered by the loader's own progress; this
// bounds only the conversion itself, so a hung worker can't strand the attachment.
const CONVERSION_TIMEOUT_MS = 90_000;

export function pdfFilenameFor(htmlFilename: string): string {
  const base = htmlFilename.replace(/\.html?$/i, "");
  return `${base || "page"}.pdf`;
}

/**
 * Renders an HTML file to PDF in the browser with the LibreOffice WASM
 * converter already used for document previews. Pages that load scripts or
 * remote assets render without them — the converter has no network access.
 */
export async function convertHtmlToPdf(
  file: File,
  {
    onLoadProgress,
    onConverting,
  }: {
    /** One-time converter download/initialisation progress. */
    onLoadProgress?: (info: LoadProgress) => void;
    /** Called once the converter is ready and the conversion itself starts. */
    onConverting?: () => void;
  } = {},
): Promise<File> {
  const { getLibreOfficeConverter, destroyLibreOfficeConverter } = await import(
    "@/lib/libreoffice-loader"
  );
  const converter = await getLibreOfficeConverter(onLoadProgress);
  onConverting?.();
  const input = new Uint8Array(await file.arrayBuffer());

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error("HTML to PDF conversion timed out")),
      CONVERSION_TIMEOUT_MS,
    );
  });

  try {
    const result = await Promise.race([
      converter.convert(input, { outputFormat: "pdf", inputFormat: "html" }, file.name),
      timeout,
    ]);
    return new File([result.data.slice(0)], pdfFilenameFor(file.name), {
      type: "application/pdf",
    });
  } catch (err) {
    // A failed or hung worker would poison the next preview or conversion.
    await destroyLibreOfficeConverter();
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
